import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Inbox, RefreshCw } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import PortfolioManager from "../components/PortfolioManager";
import BlogManager from "../components/BlogManager";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["Nuovo", "Contattato", "Chiuso"];

const statusStyle = {
  Nuovo: "bg-black text-white",
  Contattato: "bg-champagne text-white",
  Chiuso: "bg-muted text-muted-foreground",
};

export default function AdminDashboard() {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const token = localStorage.getItem("vd_token");
  const name = localStorage.getItem("vd_name") || "Admin";

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/inquiries`, authHeader);
      setInquiries(data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("vd_token");
        navigate("/admin/login");
      } else {
        toast.error("Errore nel caricamento");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    load();
    // eslint-disable-next-line
  }, []);

  const changeStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/inquiries/${id}/status`, { status }, authHeader);
      setInquiries((arr) => arr.map((i) => (i.id === id ? { ...i, status } : i)));
      toast.success("Stato aggiornato");
    } catch {
      toast.error("Aggiornamento non riuscito");
    }
  };

  const logout = () => {
    localStorage.removeItem("vd_token");
    localStorage.removeItem("vd_name");
    navigate("/admin/login");
  };

  const counts = {
    total: inquiries.length,
    nuovo: inquiries.filter((i) => i.status === "Nuovo").length,
    chiuso: inquiries.filter((i) => i.status === "Chiuso").length,
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#FAFAFA]">
      <header className="bg-white border-b border-black/10 px-6 md:px-12 py-5 flex items-center justify-between">
        <Link to="/" className="font-serif text-xl tracking-tight">
          VETRO <span className="text-champagne">DESIGN</span> · Admin
        </Link>
        <div className="flex items-center gap-4">
          <span className="text-sm text-muted-foreground hidden sm:block">{name}</span>
          <button onClick={load} data-testid="refresh-button" className="p-2 hover:bg-muted transition-colors" aria-label="Aggiorna">
            <RefreshCw strokeWidth={1.2} className="w-4 h-4" />
          </button>
          <button
            onClick={logout}
            data-testid="logout-button"
            className="flex items-center gap-2 text-sm border border-black/20 px-4 py-2 hover:bg-black hover:text-white transition-colors"
          >
            <LogOut strokeWidth={1.2} className="w-4 h-4" /> Esci
          </button>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-12 py-10">
        <div className="grid grid-cols-3 gap-4 md:gap-6 mb-10">
          {[
            { label: "Richieste totali", value: counts.total },
            { label: "Nuove", value: counts.nuovo },
            { label: "Chiuse", value: counts.chiuso },
          ].map((c) => (
            <div key={c.label} data-testid={`stat-${c.label}`} className="bg-white border border-black/10 p-6">
              <p className="font-serif text-4xl font-light">{c.value}</p>
              <p className="text-xs uppercase tracking-wide text-muted-foreground mt-2">{c.label}</p>
            </div>
          ))}
        </div>

        <PortfolioManager token={token} />

        <BlogManager token={token} />

        <div className="bg-white border border-black/10">
          <div className="px-6 py-4 border-b border-black/10 flex items-center gap-2">
            <Inbox strokeWidth={1.2} className="w-4 h-4" />
            <h2 className="text-sm uppercase tracking-[0.2em]">Richieste di Preventivo</h2>
          </div>

          {loading ? (
            <p className="p-10 text-center text-muted-foreground">Caricamento...</p>
          ) : inquiries.length === 0 ? (
            <p data-testid="empty-state" className="p-10 text-center text-muted-foreground">Nessuna richiesta al momento.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead className="hidden md:table-cell">Contatti</TableHead>
                  <TableHead className="hidden lg:table-cell">Progetto</TableHead>
                  <TableHead>Messaggio</TableHead>
                  <TableHead>Stato</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inquiries.map((i) => (
                  <TableRow key={i.id} data-testid={`inquiry-row-${i.id}`}>
                    <TableCell className="font-medium align-top">
                      {i.name}
                      <span className="block text-xs text-muted-foreground font-normal">
                        {new Date(i.created_at).toLocaleDateString("it-IT")}
                      </span>
                    </TableCell>
                    <TableCell className="hidden md:table-cell align-top text-sm">
                      <span className="block">{i.email}</span>
                      <span className="block text-muted-foreground">{i.phone || "—"}</span>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell align-top text-sm">{i.project_type}</TableCell>
                    <TableCell className="align-top text-sm max-w-xs">
                      <span className="line-clamp-3 text-muted-foreground">{i.message}</span>
                    </TableCell>
                    <TableCell className="align-top">
                      <Select value={i.status} onValueChange={(v) => changeStatus(i.id, v)}>
                        <SelectTrigger data-testid={`status-select-${i.id}`} className="w-[130px] border-0 p-0 h-auto focus:ring-0">
                          <Badge className={`${statusStyle[i.status]} rounded-none px-3 py-1 font-normal`}>{i.status}</Badge>
                        </SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((s) => (
                            <SelectItem key={s} value={s} data-testid={`status-option-${s}`}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </main>
    </div>
  );
}
